import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Query } from 'react-apollo';
import gql from 'graphql-tag';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Button,
  Chip,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { withStyles } from '@material-ui/core/styles';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import PeopleIcon from '@material-ui/icons/People';
import AddIcon from '@material-ui/icons/Add';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { Link } from 'react-router-dom';
import TenantPickerModal from './TenantPickerModal';

const styles = theme => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  filters: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    flexWrap: 'wrap',
    alignItems: 'flex-start',
  },
  filterControl: {
    minWidth: 200,
  },
  tagFilterControl: {
    minWidth: 240,
  },
  table: {
    minWidth: 650,
  },
  activeChip: {
    backgroundColor: '#4caf50',
    color: 'white',
  },
  inactiveChip: {
    backgroundColor: '#9e9e9e',
    color: 'white',
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  stepCount: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  tagChip: {
    margin: theme.spacing(0.25),
    height: 20,
    fontSize: '0.7rem',
  },
});

const AVAILABLE_TENANTS_QUERY = gql`
  query AvailableTenants {
    availableTenants {
      name
      host
    }
  }
`;

const SequenceList = ({ classes, sequences, onToggleActive, onDelete, onRefresh, currentHost }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState([]);
  const [pickerState, setPickerState] = useState({ open: false, sequenceId: null });

  const allCategories = useMemo(() => {
    const catSet = new Set();
    sequences.forEach(s => { if (s.category) catSet.add(s.category); });
    return Array.from(catSet).sort();
  }, [sequences]);

  const allTags = useMemo(() => {
    const tagSet = new Set();
    sequences.forEach(s => {
      (s.sequence_tag_list || []).forEach(tag => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, [sequences]);

  const filteredSequences = useMemo(() => {
    return sequences.filter(sequence => {
      const term = searchTerm.toLowerCase();
      const matchesSearch =
        !searchTerm ||
        sequence.name.toLowerCase().includes(term) ||
        (sequence.description || '').toLowerCase().includes(term);
      const matchesStatus =
        statusFilter === 'all' ||
        sequence.status === statusFilter;
      const matchesCategory =
        categoryFilter === 'all' || sequence.category === categoryFilter;
      const matchesTags =
        tagFilter.length === 0 ||
        tagFilter.every(tag => (sequence.sequence_tag_list || []).includes(tag));
      return matchesSearch && matchesStatus && matchesCategory && matchesTags;
    });
  }, [sequences, searchTerm, statusFilter, categoryFilter, tagFilter]);

  const handleToggleActive = (sequenceId, currentActive) => {
    if (onToggleActive) {
      onToggleActive(sequenceId, !currentActive);
    }
  };

  const handleDelete = (sequenceId, sequenceName) => {
    if (window.confirm(`Are you sure you want to delete the sequence "${sequenceName}"? This action cannot be undone.`)) {
      if (onDelete) {
        onDelete(sequenceId);
      }
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h4">Email Sequences</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          component={Link}
          to="/admin/email_sequences/new"
        >
          Create Sequence
        </Button>
      </div>

      <div className={classes.filters}>
        <TextField
          label="Search sequences"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={classes.filterControl}
        />
        <FormControl variant="outlined" size="small" className={classes.filterControl}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label="Status"
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="published">Published</MenuItem>
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" className={classes.filterControl}>
          <InputLabel>Category</InputLabel>
          <Select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            label="Category"
          >
            <MenuItem value="all">All Categories</MenuItem>
            {allCategories.map(cat => (
              <MenuItem key={cat} value={cat} style={{ textTransform: 'capitalize' }}>
                {cat}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Autocomplete
          multiple
          options={allTags}
          value={tagFilter}
          onChange={(_e, newValue) => setTagFilter(newValue)}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                key={option}
                label={option}
                size="small"
                {...getTagProps({ index })}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              label="Filter by tags"
              placeholder={tagFilter.length === 0 ? 'All tags' : ''}
            />
          )}
          className={classes.tagFilterControl}
        />
      </div>

      <TableContainer component={Paper}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Tags</TableCell>
              <TableCell align="center">Steps</TableCell>
              <TableCell align="center">Enrollments</TableCell>
              <TableCell align="center">Active Enrollments</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Active</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSequences.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography variant="body2" color="textSecondary">
                    {sequences.length === 0
                      ? 'No sequences created yet. Click "Create Sequence" to get started.'
                      : 'No sequences match your filters.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredSequences.map(sequence => (
                <TableRow key={sequence.id}>
                  <TableCell>
                    <Typography variant="body1" style={{ fontWeight: 500 }}>
                      {sequence.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {sequence.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" style={{ textTransform: 'capitalize' }}>
                      {sequence.category || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" flexWrap="wrap">
                      {(sequence.sequence_tag_list || []).map(tag => (
                        <Chip
                          key={tag}
                          label={tag}
                          size="small"
                          className={classes.tagChip}
                        />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={sequence.emailSequenceSteps?.length || 0}
                      size="small"
                      color="default"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">
                      {sequence.enrollmentCount || 0}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" style={{ color: '#4caf50', fontWeight: 500 }}>
                      {sequence.activeEnrollmentCount || 0}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={sequence.status === 'published' ? 'Published' : 'Draft'}
                      size="small"
                      className={sequence.status === 'published' ? classes.activeChip : classes.inactiveChip}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={sequence.active}
                      onChange={() => handleToggleActive(sequence.id, sequence.active)}
                      color="primary"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <div className={classes.actions}>
                      <Tooltip title="View Enrollments">
                        <IconButton
                          size="small"
                          component={Link}
                          to={`/admin/email_sequences/${sequence.id}/enrollments`}
                        >
                          <PeopleIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          component={Link}
                          to={`/admin/email_sequences/${sequence.id}/edit`}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Duplicate to Tenant">
                        <IconButton
                          size="small"
                          onClick={() => setPickerState({ open: true, sequenceId: sequence.id })}
                        >
                          <FileCopyIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(sequence.id, sequence.name)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {pickerState.sequenceId && (
        <Query query={AVAILABLE_TENANTS_QUERY}>
          {({ data }) => {
            return (
              <TenantPickerModal
                open={pickerState.open}
                onClose={() => setPickerState({ open: false, sequenceId: null })}
                tenants={(data && data.availableTenants) || []}
                recordId={pickerState.sequenceId}
                recordType="sequence"
                currentHost={currentHost}
              />
            );
          }}
        </Query>
      )}
    </div>
  );
};

SequenceList.propTypes = {
  classes:      PropTypes.object.isRequired,
  sequences:    PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      description: PropTypes.string,
      category: PropTypes.string,
      active: PropTypes.bool.isRequired,
      status: PropTypes.string,
      enrollmentCount: PropTypes.number,
      activeEnrollmentCount: PropTypes.number,
      sequence_tag_list: PropTypes.arrayOf(PropTypes.string),
      emailSequenceSteps: PropTypes.arrayOf(
        PropTypes.shape({
          id:       PropTypes.string.isRequired,
          position: PropTypes.number.isRequired,
        })
      ),
    })
  ).isRequired,
  onToggleActive: PropTypes.func,
  onDelete:       PropTypes.func,
  onRefresh:      PropTypes.func,
  currentHost:    PropTypes.string,
};

SequenceList.defaultProps = {
  currentHost: '',
};

export default withStyles(styles)(SequenceList);
